import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { Reveal } from "@/components/Reveal";
import { CONTACTS } from "@/data/content";

const SECTIONS = [
  {
    title: "1. Responsável pelo tratamento",
    body: [
      "A EcoJard é a responsável pelo tratamento dos dados pessoais recolhidos através deste website. Para qualquer questão relacionada com a proteção dos seus dados, pode contactar-nos através dos contactos indicados no final desta página.",
    ],
  },
  {
    title: "2. Dados que recolhemos",
    body: [
      "Através do formulário de pedido de orçamento, recolhemos apenas os dados necessários para responder ao seu pedido: nome, telefone, email, localidade, tipo de serviço, área aproximada do espaço, descrição do trabalho pretendido e, se o desejar, fotografias do espaço que nos enviar.",
      "Não recolhemos dados sensíveis nem utilizamos ferramentas de tracking ou cookies de publicidade neste website.",
    ],
  },
  {
    title: "3. Finalidade do tratamento",
    body: [
      "Os dados fornecidos são utilizados exclusivamente para analisar o seu pedido, apresentar uma proposta de orçamento e entrar em contacto consigo nesse âmbito, por telefone, email ou WhatsApp.",
      "O fundamento jurídico do tratamento é a diligência pré-contratual solicitada pelo titular dos dados (artigo 6.º, n.º 1, alínea b) do RGPD).",
    ],
  },
  {
    title: "4. Conservação dos dados",
    body: [
      "Os dados do seu pedido são conservados apenas pelo tempo necessário para dar resposta ao mesmo e para a eventual prestação do serviço contratado. As fotografias enviadas são guardadas no nosso servidor associadas ao respetivo pedido.",
    ],
  },
  {
    title: "5. Partilha com terceiros",
    body: [
      "A EcoJard não vende, cede nem partilha os seus dados pessoais com terceiros para fins de marketing. Os dados transitam apenas pelos fornecedores técnicos estritamente necessários ao funcionamento do website e ao envio de emails, sempre ao abrigo das obrigações legais aplicáveis.",
    ],
  },
  {
    title: "6. Os seus direitos",
    body: [
      "Nos termos do RGPD, tem direito a aceder, retificar, limitar, opor-se ao tratamento e solicitar a eliminação dos seus dados pessoais, bem como o direito à portabilidade, quando aplicável.",
      "Para exercer qualquer destes direitos, basta contactar-nos por email ou telefone. Tem ainda o direito de apresentar reclamação à Comissão Nacional de Proteção de Dados (CNPD).",
    ],
  },
  {
    title: "7. Segurança",
    body: [
      "Aplicamos medidas técnicas e organizativas adequadas para proteger os seus dados contra acesso não autorizado, perda ou destruição.",
    ],
  },
  {
    title: "8. Alterações a esta política",
    body: [
      "Esta política pode ser atualizada pontualmente. A versão em vigor é a que se encontra publicada nesta página.",
    ],
  },
];

export default function PrivacyPage() {
  return (
    <>
      <SEO
        title="Política de Privacidade | EcoJard"
        description="Política de privacidade da EcoJard: como tratamos os dados enviados através do formulário de pedido de orçamento, os seus direitos ao abrigo do RGPD e os nossos contactos."
        path="/privacidade"
      />
      <Header />
      <main data-testid="privacy-page" className="pt-28 sm:pt-36">
        <div className="mx-auto max-w-3xl px-5 pb-24 lg:px-10">
          <Reveal>
            <Link
              to="/"
              data-testid="privacy-back-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-moss transition-colors hover:text-pine"
            >
              <ArrowLeft size={16} /> Página inicial
            </Link>
          </Reveal>

          <Reveal delay={0.05}>
            <p className="mt-10 text-xs font-semibold uppercase tracking-[0.25em] text-lime-600">
              EcoJard
            </p>
            <h1 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
              Política de Privacidade
            </h1>
            <p className="mt-6 text-base leading-relaxed text-moss sm:text-lg">
              A sua privacidade é importante para nós. Esta página explica, de forma simples, como
              tratamos os dados que nos envia através deste website, em conformidade com o
              Regulamento Geral sobre a Proteção de Dados (RGPD).
            </p>
          </Reveal>

          <div className="mt-12 flex flex-col gap-10">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={0.04 * i}>
                <section>
                  <h2 className="font-serif text-xl text-ink sm:text-2xl">{s.title}</h2>
                  {s.body.map((p, j) => (
                    <p key={j} className="mt-3 text-sm leading-relaxed text-moss sm:text-base">
                      {p}
                    </p>
                  ))}
                </section>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14 rounded-3xl bg-sand/70 p-8">
              <h2 className="font-serif text-xl text-ink">Contactos para questões de dados</h2>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <a
                  data-testid="privacy-email-link"
                  href={`mailto:${CONTACTS.email}`}
                  className="flex items-center gap-2 rounded-full border border-stone2 bg-white px-5 py-3 text-sm font-semibold text-pine transition-colors hover:border-lime-400"
                >
                  <Mail size={16} className="text-lime-600" /> {CONTACTS.email}
                </a>
                <a
                  data-testid="privacy-phone-link"
                  href={CONTACTS.phoneHref}
                  className="flex items-center gap-2 rounded-full border border-stone2 bg-white px-5 py-3 text-sm font-semibold text-pine transition-colors hover:border-lime-400"
                >
                  <Phone size={16} className="text-lime-600" /> {CONTACTS.phoneDisplay}
                </a>
                <a
                  data-testid="privacy-phone2-link"
                  href={CONTACTS.phone2Href}
                  className="flex items-center gap-2 rounded-full border border-stone2 bg-white px-5 py-3 text-sm font-semibold text-pine transition-colors hover:border-lime-400"
                >
                  <Phone size={16} className="text-lime-600" /> {CONTACTS.phone2Display}
                </a>
              </div>
            </div>
          </Reveal>

          <p className="mt-10 text-xs text-moss/70">Última atualização: outubro de 2026.</p>
        </div>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </>
  );
}
